import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Flag, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { usePatientTimeline } from '@/hooks/use-interactions';
import { DataTable, SortableHeader } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { DirectionIndicator, DirectionLabel } from '@/components/common/DirectionIndicator';

// =============================================================================
// Types
// =============================================================================

export interface PatientInteractionListProps {
  /** Patient ID to fetch interactions for */
  patientId: string | undefined;
  /** Maximum number of interactions to fetch (default: 100) */
  limit?: number;
  /** Custom class name */
  className?: string;
  /** Callback when interaction is clicked (default: navigate to activity page) */
  onInteractionClick?: (interactionId: string) => void;
}

interface InteractionRow {
  id: string;
  status: string;
  channel: string;
  direction?: string | null;
  startedAt?: string | null;
  createdAt: string;
  flagged?: number;
  wasEscalated?: number;
  summary?: string | null;
}

// =============================================================================
// Column Definitions
// =============================================================================

const columns: ColumnDef<InteractionRow>[] = [
  {
    accessorKey: 'type',
    header: 'Channel',
    cell: ({ row }) => {
      const channel = row.original.channel;
      const direction = row.original.direction;
      const channelLabel =
        channel === 'voice'
          ? 'Call'
          : channel === 'sms'
            ? 'SMS'
            : channel === 'email'
              ? 'Email'
              : channel === 'portal'
                ? 'Portal'
                : channel;

      return (
        <div className="flex items-center gap-2">
          <DirectionIndicator direction={direction} channel={channel} size="md" />
          <div className="flex flex-col">
            <span className="font-medium text-sm">{channelLabel}</span>
            <DirectionLabel direction={direction} />
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'date',
    header: ({ column }) => <SortableHeader column={column}>Date</SortableHeader>,
    accessorFn: (row) => row.startedAt ?? row.createdAt,
    cell: ({ row }) => {
      const timestamp = row.original.startedAt
        ? new Date(row.original.startedAt)
        : new Date(row.original.createdAt);
      return (
        <span className="text-sm text-gray-600">
          {timestamp.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
      );
    },
  },
  {
    accessorKey: 'summary',
    header: 'Summary',
    cell: ({ row }) => {
      const summary = row.original.summary;
      if (!summary) return <span className="text-gray-400 text-sm">-</span>;
      return <span className="text-sm text-gray-600 line-clamp-1 max-w-[300px]">{summary}</span>;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      if (status === 'completed') {
        return (
          <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      }
      if (status === 'failed') {
        return (
          <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      }
      if (status === 'in_progress') {
        return (
          <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            In Progress
          </Badge>
        );
      }
      return (
        <Badge variant="secondary" className="text-xs">
          {status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'flags',
    header: '',
    cell: ({ row }) => {
      const isFlagged = row.original.flagged === 1;
      const isEscalated = row.original.wasEscalated === 1;

      if (!isFlagged && !isEscalated) return null;

      return (
        <div className="flex items-center gap-1">
          {isFlagged && <Flag className="h-4 w-4 text-red-500 fill-red-500" />}
          {isEscalated && <AlertTriangle className="h-4 w-4 text-amber-500" />}
        </div>
      );
    },
  },
];

// =============================================================================
// Component
// =============================================================================

export function PatientInteractionList({
  patientId,
  limit = 100,
  className,
  onInteractionClick,
}: PatientInteractionListProps): React.ReactElement {
  const navigate = useNavigate();
  const { data, isLoading } = usePatientTimeline(patientId, { limit });

  const interactions: InteractionRow[] = data?.items ?? [];

  const handleRowClick = (row: InteractionRow): void => {
    if (onInteractionClick) {
      onInteractionClick(row.id);
    } else {
      navigate({ to: '/activity/$interactionId', params: { interactionId: row.id } });
    }
  };

  return (
    <div className={className}>
      <DataTable
        columns={columns}
        data={interactions}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No interactions yet"
      />
    </div>
  );
}
